import UpdateIcon from '@mui/icons-material/Update';
import Box, { type BoxProps } from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { AudienceExpirationAlerts } from '@openx/components-targeting/Targeting/shared/AudienceExpirationAlerts/AudienceExpirationAlerts.tsx';
import type { Audience } from '@openx/types/targeting/audience.ts';
import isEqual from 'lodash/isEqual';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusPill, type StatusPillTone } from './StatusPill.tsx';

const StyledIndicatorBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={[
      {
        alignItems: 'center',
        display: 'flex',
        height: '100%',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

interface Props {
  audience: Audience;
  fromDeal?: boolean;
}

const normalizeAudienceStatusKey = (rawStatus: unknown): string => {
  return String(rawStatus ?? '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
};

const audienceStatusToneMap: Partial<Record<string, StatusPillTone>> = {
  ACTIVATED: 'success',
  DRAFT: 'beige',
  EXPIRED: 'neutral',
  EXPORTED: 'success',
  EXPORT_FAILED: 'pink',
  EXPORT_FAILED_TO_SYNC: 'pink',
  EXPORT_FAIL: 'pink',
  EXPORTING: 'info',
  PUBLISH_FAILED: 'orange',
  PUBLISHING: 'info',
};

const audienceStatusLabelMap: Partial<Record<string, string>> = {
  ACTIVATED: 'Activated',
  DRAFT: 'Draft',
  EXPIRED: 'Expired',
  EXPORTED: 'Exported',
  EXPORT_FAILED: 'Export Failed',
  EXPORT_FAILED_TO_SYNC: 'Export Failed To Sync',
  EXPORT_FAIL: 'Export Fail',
  EXPORTING: 'Exporting',
  PUBLISH_FAILED: 'Publish Failed',
  PUBLISHING: 'Publishing',
};

const getAudienceStatusFallbackLabel = (statusKey: string): string => {
  if (!statusKey) return '';

  return statusKey
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const AudienceCell = memo(
  ({ audience, fromDeal = true }: Props) => {
    const { t } = useTranslation();
    const statusKey = normalizeAudienceStatusKey(audience.status);
    const statusLabel = t(audienceStatusLabelMap[statusKey] ?? getAudienceStatusFallbackLabel(statusKey));
    const legacyStatusKey = statusKey ? statusKey.toLowerCase().replace(/_/g, '-') : undefined;
    const legacyStatusTestId = legacyStatusKey ? `status-${legacyStatusKey}` : undefined;
    const tone = audienceStatusToneMap[statusKey] ?? 'neutral';

    const endIcon = statusKey === 'PUBLISHING' ? <UpdateIcon sx={{ fontSize: 14 }} /> : undefined;

    return (
      <Grid
        container
        sx={{
          flexDirection: 'column',
        }}
      >
        <Grid
          wrap="nowrap"
          container
          sx={{
            gap: 0.5,
          }}
        >
          <Grid>
            <Typography variant="body1" sx={{ overflowWrap: 'break-word', wordBreak: 'normal' }}>
              {audience.name}
            </Typography>
          </Grid>
          <Grid>
            <AudienceExpirationAlerts audience={audience} fromDeal={fromDeal} sx={{ height: 28, width: 18 }} />
          </Grid>
        </Grid>
        <Grid
          sx={{
            minHeight: 28,
          }}
        >
          <StyledIndicatorBox>
            <StatusPill
              data-test={legacyStatusTestId}
              data-color={legacyStatusKey}
              label={statusLabel || t('Unknown')}
              tone={tone}
              endIcon={endIcon}
            />
          </StyledIndicatorBox>
        </Grid>
      </Grid>
    );
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
);
